// notification-toast.ts — die EINE Brücke „Notification → Toast".
//
// <pfs-notification-bell> emittiert bei JEDEM eingehenden Realtime-Fakt
// (notification.created — event-key-agnostisch, also für alle Events gleich)
// ein generisches, composed `pfs:notification`-Event mit dem gerenderten
// Subject (title/body). Dieser Helper übersetzt es in die EINE bestehende
// Toast-Pipe (dispatchToast → `pfs:toast` → <pfs-toast-host>) — identisch zu
// jedem anderen Toast (Login-Fehler, Form-422, …). KEIN neuer Toast-Mechanismus.
//
// Ein Host ruft `installNotificationToasts()` EINMAL beim Boot — Muster wie
// `installAggregatorSessionVerifier()`. Damit ist das Verhalten für ALLE
// Tenants identisch (kein per-Tenant-Sonderweg, kein Duplikat).

import { dispatchToast, type ToastVariant } from "./toast-bus.js";

export interface NotificationToastOptions {
  /** Titel, wenn das Subject keinen `title` trägt (i18n: Host setzt seine Sprache). */
  fallbackTitle?: string;
  /** Toast-Variante (Default `info` — Notifications sind neutral, nicht Fehler). */
  variant?: ToastVariant;
}

/**
 * Verdrahtet `pfs:notification` (von <pfs-notification-bell>) auf `dispatchToast`.
 * Gibt eine Cleanup-Funktion zurück (Listener-Abbau).
 */
export function installNotificationToasts(options?: NotificationToastOptions): () => void {
  const fallbackTitle = options?.fallbackTitle ?? "New notification";
  const variant: ToastVariant = options?.variant ?? "info";

  // Dedup: dieselbe Notification kann `pfs:notification` mehrfach auslösen —
  // mehrere <pfs-notification-bell>-Instanzen im DOM (Desktop/Mobile/Embed) und/
  // oder mehrere SSE-Deliveries. Pro Notification (notification_id, Fallback
  // Envelope-id) nur EINEN Toast, mit kurzer TTL-Bereinigung.
  const seen = new Map<string, number>();
  const DEDUP_TTL_MS = 5000;

  const handler = (e: Event): void => {
    const value = (e as CustomEvent<{ value?: Record<string, unknown> }>).detail?.value ?? {};
    // Das liveBus-value ist der volle Event-Envelope (types.Event: id/tenant/
    // event_key/subject/…) — title/body liegen im `subject`. Fallback auf das
    // Top-Level, falls ein Emitter das Subject direkt schickt.
    const subject = (value.subject as Record<string, unknown> | undefined) ?? value;

    const dedupId = String(subject.notification_id ?? value.id ?? "");
    if (dedupId) {
      const now = Date.now();
      for (const [k, t] of seen) if (now - t > DEDUP_TTL_MS) seen.delete(k);
      if (seen.has(dedupId)) return;
      seen.set(dedupId, now);
    }

    const rawTitle = subject.title;
    const rawBody = subject.body;
    const title = (typeof rawTitle === "string" && rawTitle.trim()) || fallbackTitle;
    const body = typeof rawBody === "string" ? rawBody : "";
    dispatchToast(variant, title, body);
  };

  document.addEventListener("pfs:notification", handler);
  return () => document.removeEventListener("pfs:notification", handler);
}
